import { useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Cards that change places by dragging (#12, the page's side of tellvo-voip-client#116). One
 * code path for the mouse and the finger, on pointer events: a card lifts once the mouse has
 * moved a little, or once a finger has held it; from the grip it lifts at once. While it is
 * up, the other cards slide to the slots they would take, and the drop sends the new order
 * once. The DOM keeps the old order until then: every card is only translated to where it
 * would be, so the layout under the hand never changes.
 */

export interface Box { left: number; top: number; right: number; bottom: number }

/** `items` with the one at `from` put at `to`, the rest closing up. */
export function moved<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

/** The slot under the point, or -1 between and outside them. */
export function slotAt(slots: readonly Box[], x: number, y: number): number {
  return slots.findIndex((s) => x >= s.left && x < s.right && y >= s.top && y < s.bottom);
}

interface Drag {
  id: string;
  from: number;
  over: number;
  /** Where the pointer took the card, and how far it has gone since. */
  x0: number;
  y0: number;
  dx: number;
  dy: number;
  slots: Box[];
  /** Let go: the card slides into its slot before the order is committed. */
  settling: boolean;
}

/** A press that may become a drag. */
interface Pending {
  id: string;
  index: number;
  x: number;
  y: number;
  touch: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

/** How far the mouse goes before a press is a drag; how long a finger holds for one; the slide home. */
const SLACK = 6;
const HOLD_MS = 320;
const SETTLE_MS = 180;

export interface DragHandles {
  /** For the element that holds the cards; the cards are its descendants marked `data-slot`. */
  listRef: (el: HTMLElement | null) => void;
  listClass: string;
  /** For each card: the press that may become a drag, and where it stands while one is on. */
  cardProps(id: string, index: number): {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onClickCapture: (e: ReactMouseEvent<HTMLElement>) => void;
    style: CSSProperties | undefined;
    className: string;
    'data-slot': number;
  };
}

/**
 * `ids` in their current order; `onDrop` gets the whole new order and the id that moved. A
 * card for which `movable` says no presses like any other but never lifts (one the phone
 * does not have yet cannot be ordered among its own).
 */
export function useDragOrder(ids: readonly string[], movable: (id: string) => boolean, onDrop: (ids: string[], id: string) => void): DragHandles {
  const [drag, setDrag] = useState<Drag | null>(null);
  const list = useRef<HTMLElement | null>(null);
  const pending = useRef<Pending | null>(null);
  const live = useRef<Drag | null>(null);
  const swallowClick = useRef(false);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const key = ids.join('\n');

  const update = (next: Drag | null) => { live.current = next; setDrag(next); };
  const clearPending = () => {
    if (pending.current?.timer) clearTimeout(pending.current.timer);
    pending.current = null;
  };
  const begin = (p: Pending, x: number, y: number) => {
    clearPending();
    const cards = list.current ? [...list.current.querySelectorAll<HTMLElement>('[data-slot]')] : [];
    const slots = cards.map((c) => c.getBoundingClientRect()).map((r) => ({ left: r.left, top: r.top, right: r.right, bottom: r.bottom }));
    if (slots.length < 2) return;
    swallowClick.current = true;
    update({ id: p.id, from: p.index, over: p.index, x0: p.x, y0: p.y, dx: x - p.x, dy: y - p.y, slots, settling: false });
  };

  // The list changed under the hand (the phone's snapshot came): the slots are stale, so let go.
  useEffect(() => {
    if (live.current && !live.current.settling) update(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const p = pending.current;
      if (p) {
        const far = Math.hypot(e.clientX - p.x, e.clientY - p.y);
        // A finger that moves before the hold is scrolling, not dragging; the mouse drags at once.
        if (p.touch && p.timer) { if (far > SLACK) clearPending(); return; }
        if (far > SLACK) begin(p, e.clientX, e.clientY);
        return;
      }
      const d = live.current;
      if (!d || d.settling) return;
      const over = slotAt(d.slots, e.clientX, e.clientY);
      update({ ...d, dx: e.clientX - d.x0, dy: e.clientY - d.y0, over: over >= 0 ? over : d.over });
    };

    const onUp = () => {
      clearPending();
      const d = live.current;
      if (!d || d.settling) return;
      if (d.over === d.from) { update(null); return; }
      // Into its slot first, then the order for real: the DOM then takes the shape the eye already sees.
      const [a, b] = [d.slots[d.from], d.slots[d.over]];
      const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      const commit = () => { update(null); onDropRef.current(moved(idsRef.current, d.from, d.over), d.id); };
      if (reduced || !a || !b) { commit(); return; }
      update({ ...d, dx: b.left - a.left, dy: b.top - a.top, settling: true });
      setTimeout(commit, SETTLE_MS);
    };

    const onCancel = () => { clearPending(); if (live.current && !live.current.settling) update(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    // The page must not scroll under a card in the air; React's own touch listeners are passive.
    const onTouchMove = (e: TouchEvent) => { if (live.current) e.preventDefault(); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchmove', onTouchMove);
      clearPending();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (id: string, index: number) => (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 || live.current || !movable(id)) return;
    const target = e.target as Element;
    // A control inside the card is its own thing to press.
    if (target.closest('button, a, input')) return;
    const touch = e.pointerType !== 'mouse';
    const grip = target.closest('[data-grip]') !== null;
    const p: Pending = { id, index, x: e.clientX, y: e.clientY, touch, timer: null };
    // A finger holds the card to lift it; on the grip, which the browser does not scroll from, it lifts at once.
    if (touch && !grip) p.timer = setTimeout(() => { p.timer = null; if (pending.current === p) begin(p, p.x, p.y); }, HOLD_MS);
    pending.current = p;
  };

  // The click that ends a drag is not a click on the card.
  const onClickCapture = (e: ReactMouseEvent<HTMLElement>) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  const styleFor = (index: number): CSSProperties | undefined => {
    if (!drag) return undefined;
    if (index === drag.from) return { transform: `translate(${drag.dx}px, ${drag.dy}px)${drag.settling ? '' : ' scale(1.02)'}` };
    // Where this card stands once the lifted one takes `over`: its own slot, or the one before or after.
    const target = index > drag.from && index <= drag.over ? index - 1 : index < drag.from && index >= drag.over ? index + 1 : index;
    const [a, b] = [drag.slots[index], drag.slots[target]];
    if (target === index || !a || !b) return undefined;
    return { transform: `translate(${b.left - a.left}px, ${b.top - a.top}px)` };
  };

  return {
    listRef: (el) => { list.current = el; },
    listClass: drag ? ' lines--dragging' : '',
    cardProps: (id, index) => ({
      onPointerDown: onPointerDown(id, index),
      onClickCapture,
      style: styleFor(index),
      className: drag?.id === id ? ` line--lifted${drag.settling ? ' line--settling' : ''}` : '',
      'data-slot': index,
    }),
  };
}
